export type NotifyLevel = "success" | "info" | "warning" | "error";

export type NotifyAction = Readonly<{
  label: string;
  onClick: () => void;
  kind?: "link" | "default";
  /**
   * Keep the notification visible after the click. Without it a UI adapter
   * may dismiss the notification, as the antd adapter does.
   */
  keepOpen?: boolean;
}>;

export type NotifyOptions = Readonly<{
  /**
   * Used for dedupe and for replacing/dismissing persistent notifications.
   */
  key?: string;
  description?: string;
  /**
   * If true, the notification should remain visible until dismissed.
   * UI adapters can map this to duration=0/Infinity.
   */
  persist?: boolean;
  /**
   * Optional duration override.
   */
  durationMs?: number;
  /**
   * Bypass the dedupe window (useful for status-toasts that must update).
   */
  bypassDedupe?: boolean;
  /**
   * Optional single action shown alongside the message (e.g. "Reload page").
   * UI adapters can map this to a link/button.
   */
  action?: NotifyAction;
  /**
   * Further actions, shown after `action` (e.g. "Details kopieren"). A UI
   * adapter that does not know them shows `action` only.
   */
  extraActions?: readonly NotifyAction[];
}>;

/**
 * The actions of a notification in the order a UI adapter shows them. A click
 * runs the action and then `dismiss`, unless the action keeps the
 * notification open.
 */
export function toRenderedActions(
  options: NotifyOptions | undefined,
  dismiss?: () => void,
): readonly NotifyAction[] {
  const actions = [
    ...(options?.action ? [options.action] : []),
    ...(options?.extraActions ?? []),
  ];
  if (!dismiss) {
    return actions;
  }
  return actions.map((action) => ({
    ...action,
    onClick: () => {
      action.onClick();
      if (!action.keepOpen) {
        dismiss();
      }
    },
  }));
}

export interface Notifier {
  notify: (
    level: NotifyLevel,
    message: string,
    options?: NotifyOptions,
  ) => void;
  dismiss: (key: string) => void;
}

export function withDedupe(
  base: Notifier,
  options: { windowMs: number },
): Notifier {
  const lastShownAt = new Map<string, number>();
  return {
    notify: (level, message, notifyOptions) => {
      if (notifyOptions?.bypassDedupe) {
        base.notify(level, message, notifyOptions);
        return;
      }
      const key = notifyOptions?.key ?? `${level}:${message}`;
      const now = Date.now();
      const last = lastShownAt.get(key);
      if (typeof last === "number" && now - last < options.windowMs) {
        return;
      }
      lastShownAt.set(key, now);
      base.notify(level, message, notifyOptions);
    },
    dismiss: (key) => {
      base.dismiss(key);
      lastShownAt.delete(key);
    },
  };
}

export const consoleNotifier: Notifier = {
  notify: (level, message, options) => {
    const desc = options?.description ? `\n${options.description}` : "";
    // eslint-disable-next-line no-console
    const fn =
      level === "error"
        ? console.error
        : level === "warning"
          ? console.warn
          : level === "info"
            ? console.info
            : console.log;
    fn(`[notify:${level}] ${message}${desc}`);
  },
  dismiss: (_key) => {
    // no-op
  },
};

// Default to a deduped console notifier to avoid log spam when no UI notifier is configured.
let globalNotifier: Notifier = withDedupe(consoleNotifier, { windowMs: 2_000 });

export function setGlobalNotifier(next: Notifier): void {
  globalNotifier = next;
}

export function getGlobalNotifier(): Notifier {
  return globalNotifier;
}
